/**
 * Recharts styling built from theme tokens, so a chart reads on dark and light.
 *
 * The tokens are complete colours (`--chart-1: #00f500`), not shadcn's bare
 * `H S% L%` triplets — write `var(--chart-1)`, never `hsl(var(--chart-1))`,
 * which is invalid CSS and makes an SVG fall back to a black fill.
 */
import type { CSSProperties } from 'react';

/** Series colours, in the order a chart should consume them. */
export const CHART_SERIES_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const;

/** Colour for the nth series, wrapping around for charts with many series. */
export function chartSeriesColor(index: number): string {
  const position = Math.trunc(index);
  if (!Number.isFinite(position)) return CHART_SERIES_COLORS[0];
  const length = CHART_SERIES_COLORS.length;
  return CHART_SERIES_COLORS[((position % length) + length) % length];
}

/** Recharts tooltip styles using Admin theme tokens (dark-mode safe). */
export const chartTooltipContentStyle: CSSProperties = {
  background: 'var(--popover)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  color: 'var(--popover-foreground)',
  fontSize: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
};

export const chartTooltipLabelStyle: CSSProperties = {
  color: 'var(--muted-foreground)',
};

export const chartTooltipItemStyle: CSSProperties = {
  color: 'var(--foreground)',
};

export const chartAxisTick = { fill: 'var(--muted-foreground)', fontSize: 11 } as const;

export const chartGridStroke = 'var(--border)';

/**
 * Recharts draws the tooltip cursor in `#ccc` by default, which on the dark
 * shell is a pale slab sitting on top of the mark it is meant to highlight.
 * The cursor shape differs by chart type, so pass the matching one rather than
 * folding it into {@link chartTooltipProps}: a bar cursor is a filled rect, a
 * line/area cursor is a stroked curve, and each ignores the other's props.
 */
export const chartBarCursor = { fill: 'var(--muted)', fillOpacity: 0.4 } as const;

export const chartLineCursor = { stroke: 'var(--muted-foreground)', strokeWidth: 1 } as const;

/** Spread onto `<Tooltip>` for the panel itself; pair with a cursor above. */
export const chartTooltipProps = {
  contentStyle: chartTooltipContentStyle,
  labelStyle: chartTooltipLabelStyle,
  itemStyle: chartTooltipItemStyle,
} as const;
